import {
  Box,
  BoxProps,
  InputLabelProps,
  styled,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { useField } from 'formik';
import { FC, ReactNode } from 'react';

import { theme } from '../../../services/theme';

export type FormTextFieldProps = TextFieldProps & {
  Label?: ReactNode;
  containerSx?: BoxProps['sx'];
  inputLabelProps?: InputLabelProps;
  name: string;
  requiredStar?: boolean;
};

export const formTextFieldInputSx = {
  height: 45,
  marginBottom: '2px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    height: 40,
    '& input': {
      padding: '5px 10px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    '& input': {
      padding: '4px 8px',
    },
    '& div': {
      fontSize: '14px',
    },
  },
};

export const formTextFieldLabelSx = {
  color: '#356482',
  fontSize: '16px',
  fontWeight: 'regular',
  lineHeight: '19px',
  marginBottom: '10px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '17px',
    marginBottom: '8px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    lineHeight: '14px',
    marginBottom: '4px',
  },
};

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#356482',
    },
  },
});

export const FormTextField: FC<FormTextFieldProps> = ({
  Label,
  containerSx,
  label,
  name,
  requiredStar = false,
  ...props
}) => {
  const [field, meta] = useField(name);

  return (
    <Box sx={{ marginBottom: '15px', ...containerSx }}>
      {Label || (
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography sx={{ ...formTextFieldLabelSx }}>{label}</Typography>
          {requiredStar && (
            <Typography
              sx={{
                color: '#e32b1e',
                fontSize: '19px',
                marginLeft: '4px',
                marginBottom: '10px',
                lineHeight: '10px',
              }}
            >
              *
            </Typography>
          )}
        </Box>
      )}
      <CustomTextField
        {...field}
        {...props}
        InputProps={{
          ...props.InputProps,
          sx: { ...formTextFieldInputSx, ...props?.InputProps?.sx },
        }}
        FormHelperTextProps={{
          sx: {
            fontSize: '10px',
            fontWeight: 600,
            lineHeight: '16px',
            margin: 0,
            marginBottom: '-16px',
            textAlign: 'right',
          },
        }}
        error={!!meta.error && !!meta.touched}
        helperText={meta.touched ? meta.error : undefined}
        sx={{ ...props.sx, width: '100%' }}
        variant="outlined"
      />
    </Box>
  );
};
